import React, { useCallback, useRef, useState } from 'react';
import ChevronDownIcon from 'mdi-react/ChevronDownIcon';
import ChevronUpIcon from 'mdi-react/ChevronUpIcon';

import { Popover } from '@xcritical/popover';
import Button from '@xcritical/button';

import { IActionsButtonsProps } from '@crm-framework/actions-buttons/src/types';
import { colors } from '@crm-framework/theme';

import { ActionListItemWrapper, CenteredContent } from '../styled';
import { IMainButtonProps } from '../types';

interface IDropdownActionsButtonProps extends IActionsButtonsProps {
  isVisibleList?: boolean;
  onOutsidePopoverClick?: (e: Event) => void;
  onClickActionsButton?: () => void;
  popoverPositionFixed?: boolean;
  mainButtonProps?: IMainButtonProps;
}

interface IActionsListProps extends IActionsButtonsProps {
  onClick: () => void;
}

const ButtonPrefix: React.VFC<{ isOpen?: boolean }> = ({ isOpen }) => (
  <CenteredContent>
    {isOpen ? <ChevronUpIcon size="16px" /> : <ChevronDownIcon size="16px" />}
  </CenteredContent>
);

const ActionsList: React.VFC<IActionsListProps> = ({ buttons, onClick }) => (
  <>
    {buttons.map((button) => (
      <ActionListItemWrapper
        key={`action-${button.title.replace(' ', '-').toLowerCase()}`}
        onClick={onClick}>
        <Button
          {...button}
          disabled={button.isDisabled}
          shouldFitContent
          appearance="menuSubItem"
          height={40}
          loaderColor={colors.PRIMARY}>
          {button.title}
        </Button>
      </ActionListItemWrapper>
    ))}
  </>
);

export const DropdownActionsButton: React.FC<IDropdownActionsButtonProps> = ({
  buttons,
  isVisibleList,
  onOutsidePopoverClick,
  onClickActionsButton,
  popoverPositionFixed = true,
  wrapperStyle,
  mainButtonProps,
}) => {
  const [isOpenActionsList, setIsOpenActionsList] = useState<boolean>();
  const isOpenList =
    isVisibleList != null
      ? isVisibleList && isOpenActionsList
      : isOpenActionsList;
  const buttonRef = useRef<HTMLButtonElement>(null);

  const onOutsideClick = useCallback(
    (e) => {
      if (!buttonRef.current?.contains(e.target)) setIsOpenActionsList(false);

      if (onOutsidePopoverClick) onOutsidePopoverClick(e);
    },
    [onOutsidePopoverClick]
  );

  const onChangeVisibleList = useCallback(() => {
    setIsOpenActionsList(!isOpenActionsList);

    if (onClickActionsButton) onClickActionsButton();
  }, [onClickActionsButton, isOpenActionsList]);

  return (
    <Popover
      content={
        <ActionsList
          buttons={buttons}
          onClick={() => setIsOpenActionsList(false)}
        />
      }
      appearance="itemList"
      visible={isOpenList}
      position="bottom left"
      trigger="click"
      positionFixed={popoverPositionFixed}
      onOutsideClick={onOutsideClick}
      convertStyles={(styles) => ({ ...styles, ...wrapperStyle })}
      autoFlip>
      <CenteredContent>
        <Button
          baseAppearance={mainButtonProps?.baseAppearance || 'primary'}
          ref={buttonRef}
          postfix={
            mainButtonProps?.prefix || (
              <ButtonPrefix isOpen={isOpenActionsList} />
            )
          }
          onClick={onChangeVisibleList}
          disabled={mainButtonProps?.disabled || false}
          appearance={mainButtonProps?.appearance}>
          <CenteredContent>
            {mainButtonProps?.title || 'Actions'}
          </CenteredContent>
        </Button>
      </CenteredContent>
    </Popover>
  );
};
